/*
 * Copyright (c) 2026.
 * Spin in het Web B.V. Apeldoorn.
 * Deze software mag alleen gebruikt / bekeken / gewijzigd worden volgens de voorwaarden die zijn vastgelegd in een bijbehorende schriftelijke overeenkomst met Spin in het Web B.V.
 */

/**
 * Auteur: Jelmer Jellema - Spin in het Web B.V.
 * Gegenereerd met Junie (AI), 03-06-2026
 *
 * Abstracte basisklasse voor de Cytoscape-canvas eilanden (`dl-modelcanvas` en `dl-simcanvas`).
 * Bevat de gedeelde logica: aanmaken en opruimen van het Cytoscape-`Core` object,
 * toepassen van een stylesheet via `cy.style().fromJson(...)`, en de aanmelding bij de
 * `CanvasregistratieService`, met de `cy`-getter voor wie de ruwe `Core` nodig heeft.
 *
 * Subclasses leveren hun eigen template (met een `#cytoContainer` div), eigen scss
 * (samen met de gedeelde `dl-cytocanvas-base.scss`) en specifieke configuratie via
 * de hook `configureerCanvas()`.
 *
 * Deze directive heeft bewust geen selector: hij is alleen een superklasse.
 */
import { Directive, ElementRef, OnDestroy, OnInit, ViewChild, inject } from '@angular/core';
import cytoscape, { Core, CytoscapeOptions, NodeSingular, Position, StylesheetJson } from 'cytoscape';
import { BaseIslandComponent } from '../../base-island.component';
import { CanvasregistratieService, type CytoCanvasEiland, type CytoCanvasView } from '../../../app/canvas/canvasregistratie.service';
// Side-effect import: registreert cytoscape collection-extensies (toIdArray, definition, outElements, inElements, lijnfunctie).
import './cytoscape-extensies';

@Directive()
export abstract class DlCytoCanvasBaseDirective extends BaseIslandComponent implements OnInit, OnDestroy, CytoCanvasEiland {

    /**
     * De view-naam waaronder dit canvas zich registreert bij de `CanvasregistratieService`.
     * Subclasses moeten deze property concreet invullen (bijv. `'model'` of `'sim'`).
     */
    protected abstract readonly viewKey: CytoCanvasView;

    /**
     * De registratie waarop dit canvas zich aanmeldt, zodat anderen via `getCanvas(view)` en
     * `whenCanvas(view)` bij de Cytoscape-`Core` kunnen.
     */
    private readonly canvasregistratie = inject(CanvasregistratieService);

    /**
     * Referentie naar de container-div waarin Cytoscape wordt gerenderd.
     * Subclasses moeten in hun template een `<div #cytoContainer>` opnemen.
     */
    @ViewChild('cytoContainer', { static: true })
    protected readonly cytoContainer!: ElementRef<HTMLDivElement>;

    /**
     * De Cytoscape-instantie van deze canvas. Pas beschikbaar na `ngOnInit`.
     */
    protected cyInstance: Core | null = null;

    /**
     * Houdt de maat van de container in de gaten. Cytoscape rekent zijn afmetingen bij het maken uit
     * en merkt een gewijzigde container niet zelf; tot golf 4e meldde de AngularJS-splitter dat met
     * `resizeCanvas()`. Een observer op de container doet dat voor élke oorzaak — de splitter, het
     * openen van de simulatie, een venster dat van maat verandert — en meet de maat op het moment dat
     * de browser hem echt heeft toegepast.
     */
    private maatwacht: ResizeObserver | null = null;

    /**
     * Een viewportbewerking die wacht tot de container een maat heeft; `null` als er niets wacht.
     * Zie `zetViewport` voor waarom dat nodig is.
     */
    private openstaandeViewport: (() => void) | null = null;

    /**
     * De ruwe Cytoscape-`Core`, of `null` vóór `ngOnInit` en na het opruimen. De
     * `CanvasregistratieService` leest hem voor `getCanvas(view)` en `whenCanvas(view)`.
     */
    public get cy(): Core | null {
        return this.cyInstance;
    }

    /**
     * Initialiseert de Cytoscape-instantie. Subclasses kunnen via `configureerCanvas`
     * extra opties meegeven; de defaults zijn bewust minimaal. Direct na het maken van
     * de Core wordt de automove-functionaliteit gekoppeld.
     */
    ngOnInit(): void {
        const opties: CytoscapeOptions = this.configureerCanvas({
            container: this.cytoContainer.nativeElement,
            elements: [],
            style: [],
            layout: { name: 'preset' }
        });
        this.cyInstance = cytoscape(opties);
        this.koppelAutomove(this.cyInstance);
        this.volgDeMaat();
        this.canvasregistratie.registreerCanvas(this.viewKey, this);
    }

    /**
     * Ruimt de Cytoscape-instantie op bij het verwijderen van het component
     * en deregistreert dit canvas bij de registratie.
     */
    ngOnDestroy(): void {
        this.canvasregistratie.deregistreerCanvas(this.viewKey, this);
        this.maatwacht?.disconnect();
        this.maatwacht = null;
        if (this.cyInstance) {
            this.cyInstance.destroy();
            this.cyInstance = null;
        }
    }

    /**
     * Laat cytoscape zijn afmetingen bijwerken zodra de container van maat verandert. De eerste
     * melding komt meteen na het aansluiten en is die van de huidige maat; die slaan we over, want
     * cytoscape heeft hem dan net zelf gemeten.
     */
    private volgDeMaat(): void {
        const container = this.cytoContainer.nativeElement;
        let eerste = true;
        this.maatwacht = new ResizeObserver(() => {
            if (eerste) {
                eerste = false;
                return;
            }
            //twee keer, zoals de AngularJS-splitter dat deed: de eerste resize kan een scrollbalk
            //oproepen of laten vallen, en dan is de tweede meting de goede
            this.cyInstance?.resize();
            this.cyInstance?.resize();
            this.voerOpenstaandeViewportUit();
        });
        this.maatwacht.observe(container);
    }

    /**
     * Voert een viewportbewerking uit — een fit, een center, een zoom — maar pas als de container
     * een maat heeft.
     *
     * Cytoscape onthoudt de maat van zijn container en meet niet uit zichzelf opnieuw. Staat het
     * canvas op dat moment op `display: none`, zoals het simulatiescherm wanneer het dicht is, dan
     * is die onthouden maat 0×0. `fit()` weigert dan netjes — daar zit een guard op — maar
     * **`center()` rekent gewoon door** en legt het middelpunt van de graaf op de linkerbovenhoek.
     * Het resultaat is een tekening die veel te ver naar boven staat, waarvan je alleen de onderkant
     * ziet. Dat gebeurde bij het terugbladeren door een replay en bij een volger die aansluit
     * terwijl er een simulatie openstaat (gevonden 23-09-2026).
     *
     * Vandaar deze omweg: is er maat, dan meteen; anders wachten tot de maatwacht zegt dat de
     * container er staat. De laatste aanvraag wint, want een oudere bewerking gaat over een stand
     * die intussen vervangen is. Haal deze guard er niet uit zonder `center()` mee te nemen.
     * @param bewerking wat er op de viewport moet gebeuren zodra dat zin heeft
     */
    protected zetViewport(bewerking: () => void): void {
        const cy = this.cyInstance;
        if (!cy) {
            return;
        }
        //de onthouden maat kan van vóór het openen zijn; opnieuw meten kost niets
        cy.resize();
        if (cy.width() > 0 && cy.height() > 0) {
            this.openstaandeViewport = null;
            bewerking();
            return;
        }
        this.openstaandeViewport = bewerking;
    }

    /**
     * Voert de bewerking uit die op een maat stond te wachten, als de container er inmiddels is.
     */
    private voerOpenstaandeViewportUit(): void {
        const cy = this.cyInstance;
        if (!this.openstaandeViewport || !cy || cy.width() <= 0 || cy.height() <= 0) {
            return;
        }
        const bewerking = this.openstaandeViewport;
        this.openstaandeViewport = null;
        bewerking();
    }

    /**
     * Past een Cytoscape-stylesheet toe op het canvas. Wordt door subclasses
     * aangeroepen wanneer een nieuwe stylesheet (bijv. `cytoModelStyle` of
     * `cytoSimStyle`) beschikbaar is.
     */
    public pasStylesheetToe(stylesheet: StylesheetJson): void {
        if (!this.cyInstance) return;
        this.cyInstance.style().fromJson(stylesheet).update();
    }

    /**
     * fitToMaxZoom: fit het model in het canvas, maar zoom niet verder in dan `maxZoom` (standaard 3).
     * Als `onlyIfNeeded` true is, wordt eerst gecontroleerd of er wel een element buiten het zichtbare
     * extent valt; zo niet, dan doet de methode niets.
     */
    public fitToMaxZoom(maxZoom?: number, onlyIfNeeded?: boolean): void {
        const cy = this.cyInstance;
        if (!cy) return;
        const padding = 44;
        if (onlyIfNeeded) {
            const vp = cy.extent();
            const bb = cy.elements().boundingBox();
            if (!(bb.x1 < (vp.x1 + padding) || bb.x2 > (vp.x2 - padding) || bb.y1 < (vp.y1 + padding) || bb.y2 > (vp.y2 - padding))) {
                return;
            }
        }
        cy.fit(undefined, padding);
        const max = maxZoom || 3;
        // laat cytoscape eerst de fit afronden, daarna eventueel terugzoomen
        setTimeout(() => {
            if (!this.cyInstance) return;
            this.cyInstance.center();
            if (this.cyInstance.zoom() > max) {
                this.cyInstance.zoom(max);
                this.cyInstance.center();
            }
        }, 100);
    }

    /**
     * emptyArea: controleert of het opgegeven rechthoekige gebied (model-coordinaten) leeg is
     * van nodes die aan `selector` voldoen. Standaard wordt gekeken naar `:visible`-nodes.
     * Retourneert `true` als er geen overlappende node is, anders `false`.
     */
    public emptyArea(left: number, right: number, top: number, bottom: number, selector?: string): boolean {
        const cy = this.cyInstance;
        if (!cy) return true;
        const sel = selector ?? ':visible';
        let overlap = false;
        cy.nodes(sel).forEach((node) => {
            const npos = node.position();
            const nwidth = node.outerWidth();
            const nheight = node.outerHeight();
            const nleft = npos.x - nwidth / 2;
            const nright = npos.x + nwidth / 2;
            const ntop = npos.y - nheight / 2;
            const nbottom = npos.y + nheight / 2;

            if (
                ((nleft >= left && nleft <= right) ||
                    (nright >= left && nright <= right) ||
                    (nleft <= left && nright >= right))
                &&
                ((ntop >= top && ntop <= bottom) ||
                    (nbottom >= top && nbottom <= bottom) ||
                    (ntop <= top && nbottom >= bottom))
            ) {
                overlap = true;
                return false; // break
            }
            return undefined;
        });
        return !overlap;
    }

    /**
     * renderedToModelCoords: reken een renderpositie (pixels op het scherm) terug naar
     * modelcoördinaten, rekening houdend met pan en zoom van het canvas.
     */
    public renderedToModelCoords(renderedPos: Position): Position {
        const cy = this.cyInstance;
        if (!cy) return { x: 0, y: 0 };
        const pan = cy.pan();
        const zoom = cy.zoom();
        return {
            x: (renderedPos.x - pan.x) / zoom,
            y: (renderedPos.y - pan.y) / zoom
        };
    }

    /**
     * Genereert een uniek ID voor een nieuwe node of edge in dit canvas.
     * Gebruikt prefix + timestamp; bij collisie wordt een teller toegevoegd.
     */
    public getId(prefix?: string): string {
        const p = prefix || 'e';
        let id = p + Date.now();
        if (this.cyInstance?.hasElementWithId(id)) {
            let teller = 2;
            while (this.cyInstance.hasElementWithId(id + teller)) {
                teller++;
            }
            id = id + teller;
        }
        return id;
    }

    /**
     * Hook voor subclasses om de Cytoscape-opties aan te vullen of te overschrijven
     * voordat `cytoscape()` wordt aangeroepen. Standaardimplementatie geeft de
     * meegegeven opties ongewijzigd terug.
     */
    protected configureerCanvas(opties: CytoscapeOptions): CytoscapeOptions {
        return opties;
    }

    /**
     * Geeft aan of bij automove ook de huidig geselecteerde nodes mee verplaatsen (naast de
     * gegrabde node en zijn automovegroep). Standaard `false`, gelijk aan de productie-ng1: zowel
     * het model- als het simcanvas riepen `initFeatures` met `multiselect` false aan. Met `true`
     * sleept een nog-geselecteerde node mee bij het draggen van een andere node — bij single
     * selection ongewenst. Een subklasse mag dit overschrijven als een canvas ooit wél
     * groepsverslepen op selectie nodig heeft.
     */
    protected get automoveMultiselect(): boolean {
        return false;
    }

    /**
     * Koppelt de automove-functionaliteit aan de gegeven Cytoscape-instantie.
     * Elementen in dezelfde `automovegroup` met een gelijke of lagere `automoveorder`
     * bewegen mee met de hoofdnode. Children bewegen sowieso mee met hun parent.
     * Bij multiselect (`automoveMultiselect === true`) worden ook de huidig
     * geselecteerde elementen toegevoegd aan de movegroup.
     *
     * Logica overgenomen uit `app/service/cytoscape.service.js` van de oude AngularJS-stack.
     */
    private koppelAutomove(cy: Core): void {
        cy.scratch('_cytoscape_service', { multiselect: this.automoveMultiselect });

        cy.on('grab', 'node', function (this: NodeSingular) {
            this.scratch('prevposition', { ...this.position() });
        });

        cy.on('drag', 'node', (event) => {
            this.doAutomove(event.target as NodeSingular);
        });

        cy.on('free', 'node', (event) => {
            this.eindigAutomove(event.target.cy());
        });
    }

    /**
     * Verzorgt de automove tijdens een drag: berekent de delta van de hoofdnode en
     * verplaatst alle nodes in de movegroup met dezelfde delta. De automovedata wordt
     * eenmaal per dragsessie opgebouwd en bewaard in `cy.scratch('automove')`.
     */
    private doAutomove(dragged: NodeSingular): void {
        const canvas = dragged.cy();
        const automove = this.haalAutomoveOp(dragged);
        if (!dragged.same(automove.mainnode)) return; // alleen op de mainnode reageren
        canvas.emit('inAutomove', [automove]);

        const pos = dragged.position();
        const prevpos = dragged.scratch('prevposition');
        const dx = pos.x - prevpos.x;
        const dy = pos.y - prevpos.y;
        if (dx === 0 && dy === 0) return;
        dragged.scratch('prevposition', { ...pos });

        automove.movegroup.forEach((node: cytoscape.SingularElementReturnValue) => {
            const np = (node as NodeSingular).position();
            (node as NodeSingular).position({ x: np.x + dx, y: np.y + dy });
        });
    }

    /**
     * Sluit een dragsessie af: emit `endAutomove` en ruimt de automovedata op.
     */
    private eindigAutomove(canvas: Core): void {
        const automove = canvas.scratch('automove');
        if (!automove) return;
        canvas.removeScratch('automove');
        canvas.emit('endAutomove', [automove]);
    }

    /**
     * Haal de automovedata voor de huidige dragsessie op, of bouw hem als hij voor
     * deze dragsessie nog uitgerekend moet worden.
     *
     * De movegroup wordt opgebouwd via een breadth-first traversal (agenda + done):
     * - seed met `:grabbed` (+ `:selected` bij `multiselect`)
     * - per node toegevoegd: `outElements('node[^nomovewithparent]')` (incl. edgeHandle-handling),
     *   directe `edgeHandle`-nodes met `handleParent === nodeId`, de parent-node van een handle
     *   (tenzij `freeMove`), en alle nodes uit dezelfde `automovegroup` met gelijke of lagere
     *   `automoveorder`.
     * - grabbed nodes worden NIET aan de movegroup toegevoegd (die verplaatst Cytoscape al).
     *
     * Logica overgenomen uit `app/service/cytoscape.service.js` (functie `getAutomove`).
     */
    private haalAutomoveOp(eventtarget: NodeSingular): { mainnode: NodeSingular; movegroup: cytoscape.CollectionReturnValue } {
        const canvas = eventtarget.cy();
        const options = canvas.scratch('_cytoscape_service') || {};

        let automove = canvas.scratch('automove');
        if (automove) return automove;

        // fix missende automoveorders (default 0)
        canvas.nodes('[automovegroup][^automoveorder]').data('automoveorder', 0);

        let movegroup = canvas.collection();
        const done = new Set<string>();
        const agenda: NodeSingular[] = [];

        // seed: bij multiselect ook :selected, anders alleen :grabbed
        const seedSelector = options.multiselect ? ':grabbed, :selected' : ':grabbed';
        canvas.nodes(seedSelector).forEach((n) => { agenda.push(n as NodeSingular); });

        let anticrash = 0;
        while (agenda.length) {
            if (++anticrash > 10000) {
                // veiligheid: oneindige loop voorkomen
                break;
            }
            const node = agenda.pop() as NodeSingular;
            const nodeId = node.id();
            if (done.has(nodeId)) continue;
            done.add(nodeId);

            // grabbed nodes verplaatst Cytoscape al zelf; alleen niet-grabbed toevoegen
            if (!node.grabbed()) {
                movegroup = movegroup.add(node);
            }

            // outElements (incl. edgeHandle-uitgangen) die mee mogen met parent
            const outs = (node as unknown as { outElements: (s: string) => cytoscape.NodeCollection })
                .outElements('node[^nomovewithparent]');
            outs.forEach((n) => { agenda.push(n as NodeSingular); });

            // edgeHandle-nodes waarvan deze node de handleParent is
            canvas.nodes('node.edgeHandle[handleParent="' + nodeId + '"]')
                .forEach((n) => { agenda.push(n as NodeSingular); });

            // is deze node zelf een edgeHandle? Dan beweegt zijn parent mee (tenzij freeMove)
            const handleParent = node.data('handleParent');
            if (handleParent && !node.data('freeMove')) {
                const parent = canvas.getElementById(handleParent);
                if (parent.nonempty()) agenda.push(parent as unknown as NodeSingular);
            }

            // alles uit dezelfde automovegroup met gelijke of lagere automoveorder
            const group = node.data('automovegroup');
            if (group) {
                const order = node.data('automoveorder') || 0;
                canvas.nodes('[automovegroup="' + group + '"][automoveorder<=' + order + ']')
                    .forEach((n) => { agenda.push(n as NodeSingular); });
            }
        }

        automove = { mainnode: eventtarget, movegroup };
        canvas.scratch('automove', automove);
        canvas.emit('initAutomove', [automove]);
        return automove;
    }
}
